import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  getPostLogoutRedirectUri,
  hasPendingB2cRedirect,
  msalConfig,
} from '../../auth.config';

/** sessionStorage marker: the exchange token this tab has already started a B2C login for. */
const ADVYZON_LOGIN_STARTED_KEY = 'advyzonExchangeLoginStarted';

/**
 * Landing point for the Advyzon SSO handoff (see Session-Minting-Design-And-Plan.md).
 * client-service redirects here (not straight to B2C) after validating Advyzon's SAML
 * assertion and minting a short-lived exchangeToken, because ui-webapp's MSAL setup uses
 * Authorization Code + PKCE — MSAL itself must originate the B2C redirect so its cached
 * code-verifier matches the eventual callback. This component's only job is to read
 * exchangeToken off the URL and let MSAL take it from there, exactly like any other login.
 *
 * Authority is set explicitly to the real custom policy (B2C_1A_SignUpSignIn, which carries
 * the exchangeToken branch) rather than relying on msalConfig.auth.authority's default value
 * — that default resolves from environment.mslProfile, which in several environment files is
 * a B2C_1_... built-in User Flow (an entirely separate mechanism from the B2C_1A_... custom
 * policy this whole integration lives in). Mirrors FirmLoginComponent.updateMsalAuthority()'s
 * exact pattern for the same reason.
 */
@Component({
  selector: 'app-advyzon-callback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './advyzon-callback.component.html',
  styleUrl: './advyzon-callback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvyzonCallbackComponent implements OnInit {
  error = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const exchangeToken = (this.route.snapshot.queryParamMap.get('exchangeToken') || '').trim();
    if (!exchangeToken) {
      this.error = true;
      return;
    }

    if (hasPendingB2cRedirect()) {
      return;
    }

    // One login per exchange token. Coming back to this page with a token we have already
    // started a login for means something navigated here after the fact — a refresh, the back
    // button, or MSAL returning to its "login request URL". Starting another login from here
    // is what produced an endless loop in live testing: the sign-in completed (token issued,
    // user recognised, profile loaded) and then this page immediately kicked off a fresh
    // login. Hand over to the normal app routing instead, which takes a signed-in user to
    // their dashboard.
    if (sessionStorage.getItem(ADVYZON_LOGIN_STARTED_KEY) === exchangeToken) {
      this.router.navigate(['/']);
      return;
    }

    const tenantName = environment.mslConfig.tenantName;
    const authority = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/B2C_1A_SignUpSignIn`;

    const startLogin = () => {
      msalConfig.auth.authority = authority;
      msalConfig.auth.redirectUri = environment.mslConfig.redirectUri;
      msalConfig.auth.postLogoutRedirectUri = getPostLogoutRedirectUri();
      msalConfig.auth.knownAuthorities = [tenantName + '.b2clogin.com'];

      sessionStorage.setItem(ADVYZON_LOGIN_STARTED_KEY, exchangeToken);
      this.msalService.loginRedirect({
        // Land back on the app root after B2C, NOT on this page. MSAL's default return target
        // is the page that started the login, which here is /advyzon-callback?exchangeToken=…;
        // returning to it re-ran this component and looped. The root runs the standard
        // post-login handling (app.component) that routes the user to their dashboard.
        redirectStartPage: environment.mslConfig.redirectUri,
        // Matches FirmLoginComponent.updateMsalAuthority()'s scopes exactly — that's the real,
        // proven-working interactive-login scope set (openid/profile/offline_access + the API
        // read scope). loginRequest (User.Read/User.Write) is what MsalGuard uses for its own
        // silent token checks, not what a fresh interactive redirect like this one should use.
        authority,
        scopes: ['openid', 'profile', 'offline_access',
          'https://' + tenantName + '.onmicrosoft.com/' + environment.mslConfig.clientId + '/read'],
        extraQueryParameters: {
          prompt: 'login',
          exchangeToken,
          // Matches <Domain>fispoke-advyzon-exchange</Domain> on the AdvyzonSessionExchange
          // claims provider in the B2C policy — without this, B2C shows the normal sign-in
          // picker instead of auto-routing into the exchange branch (confirmed via a live test:
          // sending exchangeToken alone rendered the picker page, domain_hint was the missing piece).
          domain_hint: 'fispoke-advyzon-exchange',
        },
      })
        // Subscribed deliberately. MsalService.loginRedirect() returns an Observable wrapping
        // an already-started promise, so the redirect fires either way — but without a
        // subscriber a rejection is only an unhandled promise rejection in the console, which
        // is exactly how the uninitialized-MSAL failure stayed invisible on this dead-end page.
        .subscribe({
          error: (err) => {
            console.error('Advyzon SSO: loginRedirect failed.', err);
            this.error = true;
            this.cdr.markForCheck();
          },
        });
    };

    // MSAL v5 rejects every auth API call until initialize() has resolved, with
    // BrowserAuthError "uninitialized_public_client_application". AppComponent owns that
    // initialize() call, and it has NOT finished when this component's ngOnInit runs.
    //
    // This wait is therefore unconditional, which is what distinguishes this component from
    // every other loginRedirect() caller in the app: they all fire from a click, an HTTP
    // response or a setTimeout, which land comfortably after MSAL startup, so for them the
    // race is invisible. This page logs in the instant it loads, so it loses that race every
    // time — the redirect threw, nothing was subscribed to surface it, and the user sat on
    // "Signing you in…" forever. Checking the URL for a pending interaction (the old
    // condition here) cannot detect this: MSAL being un-initialised leaves no trace in the URL.
    //
    // inProgress$ is the right signal rather than calling initialize() again: it only leaves
    // Startup once AppComponent's initialize() + handleRedirectObservable() chain has settled,
    // so it proves both that MSAL is ready and that no other interaction is mid-flight (which
    // would fail with interaction_in_progress instead).
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        // Backstop: never leave the user on a silent spinner. If MSAL startup stalls (e.g. a
        // hung authority-metadata fetch), show the error state with a way out instead.
        timeout(20000)
      )
      .subscribe({
        next: () => startLogin(),
        error: (err) => {
          console.error('Advyzon SSO: MSAL did not become ready, cannot start sign-in.', err);
          this.error = true;
          this.cdr.markForCheck();
        },
      });
  }

  goToLogin(): void {
    this.router.navigate(['/firmlogin']);
  }
}
